import React from "react";
import {
  Button,
  Loader,
  Table,
  Modal,
  Form,
  Message,
  Popup,
  Icon,
  Checkbox,
  Placeholder,
} from "semantic-ui-react";
import { apiGatewayClient, getApiGatewayClient } from "services/api";
import { store } from "services/state";
import * as AccountService from "services/accounts";
import * as YAML from "yamljs";
import { observer } from "mobx-react";

function getUsagePlanVisibility(usagePlan) {
  let hasHidden = false;
  let hasVisible = false;

  for (const api of usagePlan.apis) {
    if (api.visibility) {
      if (hasHidden) return null;
      hasVisible = true;
    } else {
      if (hasVisible) return null;
      hasHidden = true;
    }
  }

  return hasVisible;
}

export const ApiManagement = observer(
  class ApiManagement extends React.Component {
    constructor(props) {
      super(props);
      this.state = {
        modalOpen: false,
        errors: [],
        apisUpdating: [],
        userList: [],
        isShowModal: false,
        loading: true,
        selectedUserList: [],
        selectedRowId: "",
        isShowMsg: false,
      };

      this.fileInput = React.createRef();

      this.tableSort = (first, second) => {
        if (first.name !== second.name) {
          return first.name.localeCompare(second.name);
        } else {
          return first.stage.localeCompare(second.stage);
        }
      };

      this.genericTableSort = (firstIndex, secondIndex) => {
        const list = store.visibility.generic;

        if (list[firstIndex].name !== list[secondIndex].name) {
          list[firstIndex].name.localeCompare(list[secondIndex].name);
        } else {
          // compare by their index, which happens to be their id
          return firstIndex.localeCompare(secondIndex);
        }
      };

      this.usagePlanSort = (first, second) => {
        if (first.name !== second.name) {
          return first.name.localeCompare(second.name);
        } else {
          return first.id.localeCompare(second.id);
        }
      };
    }

    componentDidMount() {
      this.getApiVisibility();
      this.getRegisteredAccounts();
    }

    getRegisteredAccounts = () => {
      AccountService.fetchRegisteredAccounts().then((accounts) => {
        if (accounts.length) {
          this.setState({
            userList: accounts,
          });
        }
      });
    };

    uploadAPISpec(event) {
      const genericList = Object.values(store.visibility.generic);
      event.preventDefault();

      const files = this.fileInput.current.files;
      let swagger, swaggerObject, anyFailures;

      if (files.length > 0) {
        this.setState((prev) => ({ ...prev, errors: [] }));
        [].forEach.call(files, (file) => {
          const reader = new window.FileReader();

          reader.onload = (e) => {
            if (file.name.includes("yaml")) {
              swaggerObject = YAML.parse(e.target.result);
              swagger = JSON.stringify(swaggerObject);
            } else {
              swaggerObject = JSON.parse(e.target.result);
              swagger = JSON.stringify(swaggerObject);
            }

            if (!(swaggerObject.info && swaggerObject.info.title)) {
              anyFailures = true;
              this.setState((prev) => ({
                ...prev,
                errors: [...prev.errors, file.name],
              }));
              return;
            }

            if (genericList) {
              const isNameExist = genericList.some(
                ({ name }) => swaggerObject.info.title === name
              );
              if (isNameExist) {
                anyFailures = true;
                this.setState((prev) => ({
                  ...prev,
                  errors: [...prev.errors, file.name],
                }));
                return;
              }
            }

            if (anyFailures) {
              return;
            }

            apiGatewayClient()
              .then((app) =>
                app.post("/admin/catalog/visibility", {}, { swagger }, {})
              )
              .then((res) => {
                if (res.status === 200) {
                  this.setState((prev) => ({
                    ...prev,
                    modalOpen: Boolean(anyFailures),
                    errors: anyFailures ? prev.errors : [],
                  }));
                }
                setTimeout(() => this.getApiVisibility(), 2000);
              });
          };
          reader.readAsText(file);
        });
      }
    }

    async deleteAPISpec(apiId) {
      // this.deleteAssignedUser(apiId);
      // const swaggerRes = await getApiSwaggerInfo(apiId);
      // const swagger = swaggerRes.status === 200 ? swaggerRes.data : {};
      // const myHash = hash(swagger);
      apiGatewayClient()
        .then((app) =>
          // app.delete(`/admin/catalog/visibility/generic/${myHash}`, {}, {}, {})
          app.delete(`/admin/catalog/visibility/generic/${apiId}`, {}, {}, {})
        )
        .then((res) => {
          setTimeout(() => this.getApiVisibility(), 2000);
        });
    }

    getApiVisibility() {
      apiGatewayClient()
        .then((app) => app.get("/admin/catalog/visibility", {}, {}, {}))
        .then((res) => {
          if (res.status === 200) {
            const apiGateway = res.data.apiGateway;
            const generic = res.data.generic && Object.keys(res.data.generic);
            apiGateway.forEach((api) => {
              if (generic) {
                generic.forEach((genApi) => {
                  if (res.data.generic[`${genApi}`]) {
                    if (
                      res.data.generic[`${genApi}`].apiId === api.id &&
                      res.data.generic[`${genApi}`].stage === api.stage
                    ) {
                      api.visibility = true;
                      delete res.data.generic[`${genApi}`];
                    }
                  }
                });
              }
            });

            store.visibility = res.data;
          }
        });
    }

    updateLocalApiGatewayApis(apisList, updatedApi, parity) {
      const updatedApis = apisList.map((stateApi) => {
        if (
          stateApi.id === updatedApi.id &&
          stateApi.stage === updatedApi.stage
        ) {
          if (parity !== undefined && (parity === true || parity === false)) {
            stateApi.visibility = parity;
          } else {
            stateApi.visibility = !stateApi.visibility;
          }
        }
        return stateApi;
      });

      store.visibility = {
        generic: store.visibility.generic,
        apiGateway: updatedApis,
      };
    }

    showApiGatewayApi(api) {
      apiGatewayClient()
        .then((app) =>
          app.post(
            "/admin/catalog/visibility",
            {},
            {
              apiKey: `${api.id}_${api.stage}`,
              subscribable: `${api.subscribable}`,
            },
            {}
          )
        )
        .then((res) => {
          if (res.status === 200) {
            this.updateLocalApiGatewayApis(store.visibility.apiGateway, api);
          }
        });
    }

    hideApiGatewayApi(api) {
      if (!api.subscribable && !api.id && !api.stage) {
        this.deleteAPISpec(api.genericId);
      } else {
        apiGatewayClient()
          .then((app) =>
            app.delete(
              `/admin/catalog/visibility/${api.id}_${api.stage}`,
              {},
              {},
              {}
            )
          )
          .then((res) => {
            if (res.status === 200) {
              this.updateLocalApiGatewayApis(store.visibility.apiGateway, api);
            }
          });
      }
    }

    showAllApiGatewayApis(usagePlan) {
      Promise.all(
        usagePlan.apis.map((api) =>
          apiGatewayClient()
            .then((app) =>
              app.post(
                "/admin/catalog/visibility",
                {},
                {
                  apiKey: `${api.id}_${api.stage}`,
                  subscribable: `${api.subscribable}`,
                },
                {}
              )
            )
            .then((res) => {
              res.api = api;
              return res;
            })
        )
      ).then((promises) => {
        promises.forEach((result) => {
          if (result.status === 200) {
            this.updateLocalApiGatewayApis(
              store.visibility.apiGateway,
              result.api,
              true
            );
          }
        });
      });
    }

    hideAllApiGatewayApis(usagePlan) {
      Promise.all(
        usagePlan.apis.map((api) =>
          apiGatewayClient()
            .then((app) =>
              app.delete(
                `/admin/catalog/visibility/${api.id}_${api.stage}`,
                {},
                {},
                {}
              )
            )
            .then((res) => {
              res.api = api;
              return res;
            })
        )
      ).then((promises) => {
        promises.forEach((result) => {
          if (result.status === 200) {
            this.updateLocalApiGatewayApis(
              store.visibility.apiGateway,
              result.api,
              false
            );
          }
        });
      });
    }

    isUpdatingApiGatewayApi(api) {
      return this.state.apisUpdating.includes(`${api.id}_${api.stage}`);
    }

    updateApiGatewayApi(api) {
      // Simpler than implementing a multiset, and probably also faster.
      this.setState(({ apisUpdating }) => ({
        apisUpdating: [...apisUpdating, `${api.id}_${api.stage}`],
      }));
      apiGatewayClient()
        .then((app) =>
          app.post(
            "/admin/catalog/visibility",
            {},
            {
              apiKey: `${api.id}_${api.stage}`,
              subscribable: `${api.subscribable}`,
            },
            {}
          )
        )
        .then(() =>
          this.setState(({ apisUpdating }) => {
            const index = apisUpdating.indexOf(`${api.id}_${api.stage}`);
            const newApisUpdating = apisUpdating.slice();
            newApisUpdating.splice(index, 1);
            return { apisUpdating: newApisUpdating };
          })
        );
    }

    isSdkGenerationConfigurable(api) {
      return api.visibility;
    }

    toggleSdkGeneration(apisList, updatedApi) {
      apiGatewayClient()
        .then((app) => {
          if (updatedApi.sdkGeneration) {
            return app.delete(
              `/admin/catalog/${updatedApi.id}_${updatedApi.stage}/sdkGeneration`,
              {},
              {},
              {}
            );
          } else {
            return app.put(
              `/admin/catalog/${updatedApi.id}_${updatedApi.stage}/sdkGeneration`,
              {},
              {},
              {}
            );
          }
        })
        .then((res) => {
          if (res.status === 200) {
            const updatedApis = apisList.map((stateApi) => {
              if (
                stateApi.id === updatedApi.id &&
                stateApi.stage === updatedApi.stage
              ) {
                stateApi.sdkGeneration = !stateApi.sdkGeneration;
              }
              return stateApi;
            });

            store.visibility.apiGateway = updatedApis;
          }
        });
    }

    renderHeaderVisibilityButton(usagePlan) {
      const usagePlanVisibility = getUsagePlanVisibility(usagePlan);

      // Some APIs are visible, some are hidden. Show the current state (Partial, with a warning) and enable all on click
      if (usagePlanVisibility == null) {
        return (
          <Popup
            content="Users subscribed to any of the APIs in this usage plan will have a valid API key for all APIs in this usage plan, even those that are not visible!"
            trigger={
              <Button
                basic
                color="yellow"
                style={{
                  backgroundColor: "white",
                  width: "100%",
                  paddingLeft: "1em",
                  paddingRight: "1em",
                  minWidth: "88px",
                }}
                onClick={() => this.showAllApiGatewayApis(usagePlan)}
              >
                Partial{" "}
                <Icon name="warning sign" style={{ paddingLeft: "5px" }} />
              </Button>
            }
          />
        );
      }

      // Either all APIs are visible or none are visible. Toggle this state on click.
      return (
        <Button
          basic
          color={usagePlanVisibility ? "green" : "red"}
          style={{ backgroundColor: "white", width: "100%" }}
          onClick={() => {
            if (usagePlanVisibility) this.hideAllApiGatewayApis(usagePlan);
            else this.showAllApiGatewayApis(usagePlan);
          }}
        >
          {usagePlanVisibility ? "True" : "False"}
        </Button>
      );
    }

    sortByUsagePlan() {
      if (!store.visibility.apiGateway) {
        return this.renderNoApis();
      }

      const usagePlans = store.visibility.apiGateway
        .filter((api) => api.usagePlanId)
        .reduce((accumulator, api) => {
          if (
            !accumulator.find((usagePlan) => api.usagePlanId === usagePlan.id)
          ) {
            accumulator.push({ id: api.usagePlanId, name: api.usagePlanName });
          }
          return accumulator;
        }, [])
        .sort(this.usagePlanSort)
        .map((usagePlan) => {
          return {
            ...usagePlan,
            apis: store.visibility.apiGateway
              .filter((api) => api.usagePlanId === usagePlan.id)
              .sort(this.tableSort),
          };
        });
      const unsubscribable = store.visibility.apiGateway
        .filter((api) => !api.usagePlanId)
        .sort(this.tableSort);
      return (
        <>
          {usagePlans.map((usagePlan) => {
            return (
              <React.Fragment key={usagePlan.id}>
                {this.renderHeader(usagePlan)}
                {this.renderApiList(usagePlan.apis)}
              </React.Fragment>
            );
          })}
          <Table.Row style={{ backgroundColor: "#1678c2", color: "white" }}>
            <Table.Cell colSpan="7">
              <b>Not Subscribable</b> <i>No Usage Plan</i>
            </Table.Cell>
          </Table.Row>
          {this.renderApiList(unsubscribable)}
        </>
      );
    }

    renderNoApis() {
      return (
        <Table.Row>
          <Table.Cell colSpan="4">No APIs found</Table.Cell>
        </Table.Row>
      );
    }

    renderHeader(usagePlan) {
      return (
        <Table.Row style={{ backgroundColor: "#1678c2", color: "white" }}>
          <Table.Cell colSpan="3">
            <b>{usagePlan && usagePlan.name}</b> <i>Usage Plan</i>
          </Table.Cell>
          <Table.Cell>
            {this.renderHeaderVisibilityButton(usagePlan)}
          </Table.Cell>
          <Table.Cell colSpan="3">{/* Intentionally empty */}</Table.Cell>
        </Table.Row>
      );
    }

    renderApiList(apis) {
      return (
        <>
          {apis
            .filter((api) => api.id !== window.config.restApiId)
            .map((api) => (
              <React.Fragment
                key={api.stage ? `${api.id}_${api.stage}` : api.id}
              >
                <Table.Row>
                  <Table.Cell collapsing>{api.name}</Table.Cell>
                  <Table.Cell>{api.stage}</Table.Cell>
                  <Table.Cell>
                    {api.subscribable ? "Subscribable" : "Not Subscribable"}
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      basic
                      color={api.visibility ? "green" : "red"}
                      style={{ width: "100%" }}
                      onClick={() =>
                        api.visibility
                          ? this.hideApiGatewayApi(api)
                          : this.showApiGatewayApi(api)
                      }
                    >
                      {api.visibility ? "True" : "False"}
                    </Button>
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      basic
                      color="blue"
                      disabled={!api.visibility}
                      style={{ width: "100%" }}
                      onClick={() => this.updateApiGatewayApi(api)}
                    >
                      {this.isUpdatingApiGatewayApi(api) ? (
                        <Loader active inline size="mini" />
                      ) : (
                        "Update"
                      )}
                    </Button>
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      basic
                      // color={api.sdkGeneration ? 'green' : 'red'}
                      color="blue"
                      style={{ width: "100%" }}
                      disabled={
                        !api.visibility ||
                        !this.isSdkGenerationConfigurable(api)
                      }
                      onClick={() =>
                        this.toggleSdkGeneration(
                          store.visibility.apiGateway,
                          api
                        )
                      }
                    >
                      {api.sdkGeneration ? "Enabled" : "Disabled"}
                    </Button>
                  </Table.Cell>
                </Table.Row>
              </React.Fragment>
            ))}
        </>
      );
    }

    assignUsers(api) {
      this.setState({ selectedRowId: `${api.id}_${api.stage}` }, () => {
        this.handleModalVisibility(true);
      });
    }

    addAssignedUser(id, UserId) {
      const params = {
        operation: "create",
        tableName: "DevPortalClientApi",
        payload: {
          Item: {
            ApiId: id,
            UserId: UserId,
          },
        },
      };
      this.operateAssignedUsers(params);
    }

    updateAssignedUser(id, UserId) {
      const params = {
        operation: "update",
        tableName: "DevPortalClientApi",
        payload: {
          Key: {
            ApiId: id,
          },
          AttributeUpdates: {
            UserId: {
              Action: "PUT",
              Value: UserId,
            },
          },
        },
      };
      this.operateAssignedUsers(params);
    }

    deleteAssignedUser(id) {
      const params = {
        operation: "delete",
        tableName: "DevPortalClientApi",
        payload: {
          Key: {
            ApiId: id,
          },
        },
      };
      this.operateAssignedUsers(params);
    }

    getAssignedUser() {
      this.setState({ loading: true });
      const { selectedRowId } = this.state;
      const params = {
        operation: "read",
        tableName: "DevPortalClientApi",
        payload: {
          Key: {
            ApiId: selectedRowId,
          },
        },
      };
      this.operateAssignedUsers(params).then((res) => {
        let selectedUserList = res && res.Item && res.Item.UserId;
        selectedUserList = selectedUserList ? selectedUserList.split(",") : [];
        this.setState({ selectedUserList, loading: false });
      });
    }

    operateAssignedUsers(params) {
      return getApiGatewayClient("/admin/developer", params);
    }

    toggleSelectAll(e, data) {
      if (data.checked) {
        const { userList } = this.state;
        let selectedUserList = userList.map((item) => {
          return item.UserId;
        });
        this.setState({ selectedUserList });
      } else {
        this.setState({ selectedUserList: [] });
      }
    }

    onDismissMsg(e, data) {
      // console.log("ondismiss");
      this.setState({ isShowMsg: false });
    }

    toggleSelect(userId) {
      const { selectedUserList } = this.state;
      const index = selectedUserList.indexOf(userId);
      if (index > -1) {
        selectedUserList.splice(index, 1);
      } else {
        selectedUserList.push(userId);
      }
      this.setState({ selectedUserList });
    }

    handleModalVisibility = (flag) => {
      const isShowModal = !!flag;
      this.setState({ isShowModal });
      if (isShowModal) {
        this.getAssignedUser();
      } else {
        this.setState({ selectedUserList: [] });
      }
    };

    onSave = () => {
      const { selectedRowId, selectedUserList } = this.state;
      const params = {
        operation: "read",
        tableName: "DevPortalClientApi",
        payload: {
          Key: {
            ApiId: selectedRowId,
          },
        },
      };
      this.operateAssignedUsers(params)
        .then((res) => {
          if (res.Item) {
            if (selectedUserList.length) {
              this.updateAssignedUser(
                selectedRowId,
                selectedUserList.join(",")
              );
            } else {
              this.deleteAssignedUser(selectedRowId);
            }
          } else {
            if (selectedUserList.length) {
              this.addAssignedUser(selectedRowId, selectedUserList.join(","));
            }
          }
        })
        .then(() => {
          this.setState({ isShowMsg: true, isShowModal: false });
        });
    };

    renderModal() {
      const { selectedUserList, isShowModal, userList, loading } = this.state;

      const isSelectAllChecked = selectedUserList.length === userList.length;
      const isSelectAllIndeterminate =
        selectedUserList.length > 0 &&
        selectedUserList.length < userList.length;
      return (
        <Modal
          size="small"
          open={isShowModal}
          onClose={() => this.handleModalVisibility()}
        >
          <Modal.Header>Assign Users</Modal.Header>
          <Modal.Content>
            <Table celled collapsing style={{ width: "100%" }}>
              <Table.Header fullWidth>
                <Table.Row>
                  <Table.HeaderCell>
                    <Checkbox
                      indeterminate={isSelectAllIndeterminate}
                      checked={isSelectAllChecked}
                      onChange={(e, data) => this.toggleSelectAll(e, data)}
                    />
                  </Table.HeaderCell>
                  <Table.HeaderCell width="4">Email address</Table.HeaderCell>
                  <Table.HeaderCell width="4">Date registered</Table.HeaderCell>
                  <Table.HeaderCell width="4">
                    Registration method
                  </Table.HeaderCell>
                  <Table.HeaderCell width="4">API Key ID</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {loading
                  ? userList.map((item) => {
                      return (
                        <Table.Row key={item.UserId}>
                          <Table.Cell>
                            <Placeholder>
                              <Placeholder.Paragraph>
                                <Placeholder.Line />
                              </Placeholder.Paragraph>
                            </Placeholder>
                          </Table.Cell>
                          <Table.Cell>
                            <Placeholder>
                              <Placeholder.Paragraph>
                                <Placeholder.Line />
                              </Placeholder.Paragraph>
                            </Placeholder>
                          </Table.Cell>
                          <Table.Cell>
                            <Placeholder>
                              <Placeholder.Paragraph>
                                <Placeholder.Line />
                              </Placeholder.Paragraph>
                            </Placeholder>
                          </Table.Cell>
                          <Table.Cell>
                            <Placeholder>
                              <Placeholder.Paragraph>
                                <Placeholder.Line />
                              </Placeholder.Paragraph>
                            </Placeholder>
                          </Table.Cell>
                          <Table.Cell>
                            <Placeholder>
                              <Placeholder.Paragraph>
                                <Placeholder.Line />
                              </Placeholder.Paragraph>
                            </Placeholder>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })
                  : userList.map((item) => {
                      const checked =
                        selectedUserList.indexOf(item.UserId) > -1;

                      return (
                        <Table.Row
                          onClick={() => this.toggleSelect(item.UserId)}
                          key={item.UserId}
                          active={checked}
                        >
                          <Table.Cell>
                            <Checkbox checked={checked} />
                          </Table.Cell>
                          <Table.Cell>{item.EmailAddress}</Table.Cell>
                          <Table.Cell>{item.DateRegistered}</Table.Cell>
                          <Table.Cell>{item.RegistrationMethod}</Table.Cell>
                          <Table.Cell>{item.ApiKeyId}</Table.Cell>
                        </Table.Row>
                      );
                    })}
              </Table.Body>
            </Table>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={() => this.handleModalVisibility()}>Cancel</Button>
            <Button onClick={() => this.onSave()}>Save</Button>
          </Modal.Actions>
        </Modal>
      );
    }

    render() {
      const { isShowMsg } = this.state;
      return (
        <>
          <Message success hidden={!isShowMsg} header="success!" />
          <div style={{ display: "flex", width: "100%" }}>
            <div style={{ padding: "2em" }}>
              <Table celled collapsing>
                <Table.Header fullWidth>
                  <Table.Row>
                    <Table.HeaderCell colSpan="6">
                      API Gateway APIs
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Header fullWidth>
                  <Table.Row>
                    <Table.HeaderCell collapsing sorted="ascending">
                      API Name
                    </Table.HeaderCell>
                    <Table.HeaderCell>Stage</Table.HeaderCell>
                    <Table.HeaderCell>API Type</Table.HeaderCell>
                    <Table.HeaderCell>Displayed</Table.HeaderCell>
                    <Table.HeaderCell>Update</Table.HeaderCell>
                    <Table.HeaderCell>Allow Generating SDKs</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>{this.sortByUsagePlan()}</Table.Body>
              </Table>
            </div>

            <div style={{ padding: "2em" }}>
              <Table celled collapsing>
                <Table.Header fullWidth>
                  <Table.Row>
                    <Table.HeaderCell colSpan="4">
                      Generic APIs
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Header fullWidth>
                  <Table.Row>
                    <Table.HeaderCell colSpan="2">
                      <Modal
                        closeIcon
                        closeOnEscape
                        closeOnDimmerClick
                        onClose={() =>
                          this.setState((prev) => ({
                            ...prev,
                            modalOpen: false,
                          }))
                        }
                        trigger={
                          <Button
                            floated="right"
                            onClick={() =>
                              this.setState((prev) => ({
                                ...prev,
                                modalOpen: true,
                              }))
                            }
                          >
                            Add API
                          </Button>
                        }
                        open={this.state.modalOpen}
                      >
                        <Modal.Header>
                          Select .JSON, .YAML, or .YML files
                        </Modal.Header>
                        <Modal.Content>
                          <>
                            <Form onSubmit={(e) => this.uploadAPISpec(e)}>
                              <Form.Field>
                                <label htmlFor="files">Select Files:</label>
                                <input
                                  type="file"
                                  id="files"
                                  name="files"
                                  accept=".json,.yaml,.yml"
                                  multiple
                                  ref={this.fileInput}
                                />
                              </Form.Field>
                              {!!this.state.errors.length && (
                                <Message
                                  size="tiny"
                                  color="red"
                                  list={this.state.errors}
                                  header="These files are not parseable or do not contain an valid api title:"
                                />
                              )}
                              <br />
                              <Button type="submit">Upload</Button>
                            </Form>
                          </>
                        </Modal.Content>
                      </Modal>
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Header fullWidth>
                  <Table.Row>
                    <Table.HeaderCell collapsing sorted="ascending">
                      API Name
                    </Table.HeaderCell>
                    <Table.HeaderCell>Delete</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {store.visibility.generic ? (
                    Object.keys(store.visibility.generic)
                      .sort(this.genericTableSort)
                      .map((apiId) => (
                        <Table.Row key={apiId}>
                          <Table.Cell collapsing>
                            {store.visibility.generic[apiId].name}
                          </Table.Cell>
                          <Table.Cell>
                            <Button
                              basic
                              color="red"
                              onClick={() => this.deleteAPISpec(apiId)}
                            >
                              Delete
                            </Button>
                          </Table.Cell>
                        </Table.Row>
                      ))
                  ) : (
                    <Table.Row>
                      <Table.Cell colSpan="4">No APIs found</Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
            </div>
            {this.renderModal()}
          </div>
        </>
      );
    }
  }
);
